import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import AppButton from "../../shared/AppButton";

function GenerateModal({
  handleClose,
  open,
  generatePayload,
  setGeneratePayload,
  handleClick,
}) {
  const { t } = useTranslation();

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={handleClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-sm bg-white rounded-xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                  <Dialog.Title className="text-sm font-bold text-[#1e293b] uppercase tracking-wide">
                    {t("Generate Invoice/UCAF")}
                  </Dialog.Title>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600 transition-opacity"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Body */}
                <div className="p-4 space-y-4">
                  {/* Invoice Toggle */}
                  <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg border border-gray-100">
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-tight">
                      {t("Invoice")}
                    </label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        id="toggle-invoice"
                        type="checkbox"
                        className="sr-only peer"
                        checked={generatePayload.invoice}
                        onChange={() =>
                          setGeneratePayload((prev) => ({
                            ...prev,
                            invoice: !prev.invoice,
                          }))
                        }
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#0070C2]" />
                    </label>
                  </div>

                  {/* UCAF Toggle */}
                  <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg border border-gray-100">
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-tight">
                      {t("UCAF")}
                    </label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        id="toggle-ucaf"
                        type="checkbox"
                        className="sr-only peer"
                        checked={generatePayload.ucaf}
                        onChange={() =>
                          setGeneratePayload((prev) => ({
                            ...prev,
                            ucaf: !prev.ucaf,
                          }))
                        }
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#0070C2]" />
                    </label>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50">
                  <AppButton
                    label={t("Cancel")}
                    variant="outlined"
                    onClick={handleClose}
                    className="!text-gray-600 !border-gray-300 hover:!bg-gray-100"
                  />
                  <AppButton
                    label={t("Generate")}
                    onClick={() => {
                      if (generatePayload.invoice || generatePayload.ucaf) {
                        handleClose();
                        handleClick(generatePayload).then(() => {
                          setGeneratePayload({ invoice: false, ucaf: false });
                        });
                      } else {
                        toast.error(t("Please Select Invoice or UCAF First"));
                      }
                    }}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default GenerateModal;
